import React, { useState, useEffect, useRef } from 'react';
import { getValidDomain } from '../config/domains';
import { getAuthKey } from '../config/secrets';
import ReCAPTCHA from 'react-google-recaptcha';

// Dynamically import all header images from the domains directory
const headerContext = require.context('../domains', true, /img\/header\.(jpg|png|jpeg)$/);
const defaultHeader = require('../shared/assets/img/header.jpg'); // fallback image

const RECAPTCHA_SITE_KEY =
  process.env.NODE_ENV === 'production'
    ? '6Ldg_jsrAAAAADnrP3ecBkwVHOaqZXmfWHBxdoFU'
    : '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

const Hero = ({ data, ipAddress }) => {
  const { title, subtitle, interestRates, ctaButton, formSection } = data;
  
  // Use normalized domain name if available, otherwise use regular validation
  let domainName;
  if (window.normalizedDomainName) {
    domainName = window.normalizedDomainName;
  } else {
    domainName = getValidDomain(data.domainName || 'localhost');
  }

  let headerImage;
  try {
    headerImage = headerContext(`./${domainName}/assets/img/header.jpg`);
  } catch {
    headerImage = defaultHeader;
  }

  const formConfig = data.formOptions?.hero || {
    fields: {
      firstName: { enabled: true },
      lastName: { enabled: true },
      email: { enabled: true },
      zipCode: { enabled: true },
      city: { enabled: false },
      message: { enabled: false }
    },
    submitText: 'Send Request'
  };

  const enabledFields = Object.entries(formConfig.fields).filter(([_, v]) => v.enabled).map(([k]) => k);

  const initialFormData = Object.fromEntries(enabledFields.map(f => [f, '']));

  const [formData, setFormData] = useState(initialFormData);
  const [submitted, setSubmitted] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [authKey, setAuthKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const formRef = useRef(null);
  const thankYouRef = useRef(null);

  useEffect(() => {
    setFormData(Object.fromEntries(enabledFields.map(f => [f, ''])));
  }, [data.domainName]);

  useEffect(() => {
    // Get auth key on component mount
    setAuthKey(getAuthKey());
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    
    // Direct DOM manipulation for immediate UI feedback
    if (formRef.current && thankYouRef.current) {
      formRef.current.style.display = 'none';
      thankYouRef.current.style.display = 'block';
    }
    
    // Use normalized domain name for consistency between www and non-www
    const submissionDomain = window.normalizedDomainName || data.domainName;
    
    const submissionData = {
      authKey,
      ipAddress: ipAddress,
      sourceUrl: window.location.href,
      domainName: submissionDomain,
      recaptchaToken,
    };
    enabledFields.forEach(f => {
      submissionData[f] = formData[f];
    });
    
    try {
      // Update state variables
      setShowForm(false);
      setSubmitted(true);
      
      // Reset form and recaptcha
      setFormData(Object.fromEntries(enabledFields.map(f => [f, ''])));
      setRecaptchaToken('');
      
      // Send the request in the background
      sendFormData(submissionData);
    } catch (error) {
      console.error('An error occurred while preparing the form:', error);
      alert('An error occurred. Please try again later.');
      
      // Revert UI changes on error
      if (formRef.current && thankYouRef.current) {
        formRef.current.style.display = 'block';
        thankYouRef.current.style.display = 'none';
      }
      
      setShowForm(true);
      setSubmitted(false);
      setSubmitting(false);
    }
  };

  // Separate function to handle the actual API call
  const sendFormData = async (data) => {
    try {
      const response = await fetch('https://vi76zjzy3k.execute-api.us-west-2.amazonaws.com/production/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Form submission failed', errorData);
        // If needed, you could still set submitted back to false here
        // but we're keeping the thank you message showing regardless
      }
    } catch (error) {
      console.error('An error occurred during API call:', error);
      // API errors won't affect the thank you message display
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <header
      id="page_top"
      style={{
        backgroundImage: `url(${headerImage})`
      }}
    >
      <div className="overlay"></div>
      <div className="container" style={{ marginTop: '50px' }}>
        <div className="row">
          <div className="col-sm-7">
            <div className="header-content">
              <div className="header-content-inner">
                <h1>{title}</h1>
                <p>{subtitle}</p>
                {interestRates.map((rate, index) => (
                  <h2 key={index}>
                    {rate.rate} <span>{rate.description}</span>
                  </h2>
                ))}
                <a href={ctaButton.link} className="btn btn-outline btn-xl page-scroll">
                  {ctaButton.text}
                </a>
              </div>
            </div>
          </div>
          <div className="col-sm-5">
            <div className="form-container">
              <div className="form-mockup">
                <h2>{formSection.heading}</h2>
                <h4>{formSection.subheading}</h4>
                
                {/* Thank you message - initially hidden */}
                <div ref={thankYouRef} style={{ display: !showForm ? 'block' : 'none' }}>
                  <p className="thank-you-message">{data.thankYouMessage || 'Thank you for your submission!'}</p>
                </div>
                
                {/* Form - initially visible */}
                <div ref={formRef} style={{ display: showForm ? 'block' : 'none' }}>
                  <form onSubmit={handleSubmit}>
                    {formConfig.fields.firstName?.enabled && (
                      <div className="form-group">
                        <input
                          type="text"
                          name="firstName"
                          className="form-control"
                          placeholder="First Name"
                          value={formData.firstName || ''}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    )}
                    {formConfig.fields.lastName?.enabled && (
                      <div className="form-group">
                        <input
                          type="text"
                          name="lastName"
                          className="form-control"
                          placeholder="Last Name"
                          value={formData.lastName || ''}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    )}
                    {formConfig.fields.email?.enabled && (
                      <div className="form-group">
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          placeholder="E-mail"
                          value={formData.email || ''}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    )}
                    {formConfig.fields.zipCode?.enabled && (
                      <div className="form-group">
                        <input
                          type="text"
                          name="zipCode"
                          className="form-control"
                          placeholder="Zip Code"
                          value={formData.zipCode || ''}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    )}
                    {formConfig.fields.city?.enabled && (
                      <div className="form-group">
                        <input
                          type="text"
                          name="city"
                          className="form-control"
                          placeholder="City"
                          value={formData.city || ''}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    )}
                    {formConfig.fields.message?.enabled && (
                      <div className="form-group">
                        <textarea
                          name="message"
                          className="form-control"
                          rows="5"
                          placeholder="Type Message Text"
                          value={formData.message || ''}
                          onChange={handleInputChange}
                          required
                        ></textarea>
                      </div>
                    )}
                    <div className="recaptcha-center">
                      <ReCAPTCHA
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={token => setRecaptchaToken(token)}
                        style={{ marginBottom: 16 }}
                      />
                    </div>
                    <button type="submit" className="btn btn-default quote_btn" disabled={submitting || !recaptchaToken}>
                      {submitting ? (
                        <span className="spinner" style={{marginRight: 8, display: 'inline-block', verticalAlign: 'middle', width: 18, height: 18}}>
                          <svg width="18" height="18" viewBox="0 0 50 50"><circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="5" strokeDasharray="90,150" strokeLinecap="round"><animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite"/></circle></svg>
                        </span>
                      ) : null}
                      {submitting ? 'Submitting...' : (formConfig.submitText || 'Submit')}
                    </button>
                    <div style={{ fontSize: '0.85em', marginTop: '10px', color: '#666' }}>
                      By signing up you are agreeing to our
                      <a href="/terms" style={{ margin: '0 4px' }} target="_blank" rel="noopener noreferrer">Terms</a>
                      &
                      <a href="/privacy-policy" style={{ margin: '0 4px' }} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Hero;

